import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAlert } from '../Components/AlertContext';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import { ConfirmationProvider } from '../Components/ConfirmationContext';
import { CustomInput, CustomSelect, CustomToggle } from '../Components/CustomFormElements';
import PageLoader from '../Components/PageLoader';
import { AiKeyApiProvider, useAiKeyApi } from '../Services/AiKeyApiProvider';
import { AiKeyTypeOption, SelectOption } from '../Services/AiKeyApiProviderTypes';

function AiKeyDetailContent() {
  const { organizationId, aiKeyId } = useParams();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { getAiKeyOptions, getAiKeyById, createAiKey, updateAiKey } = useAiKeyApi();

  const isNew = !aiKeyId || aiKeyId === 'new-ai-key';

  const [loading, setLoading] = useState<boolean>(true);
  const [isDisabled] = useState<boolean>(false);

  const [typeOptionList, setTypeOptionList] = useState<AiKeyTypeOption[]>([]);

  const [type, setType] = useState<string>('');
  const [model, setModel] = useState<string>('');
  const [aiKey, setAiKey] = useState<string>('');
  const [endpoint, setEndpoint] = useState<string>('');
  const [deployment, setDeployment] = useState<string>('');
  const [apiVersion, setApiVersion] = useState<string>('');
  const [isActive, setIsActive] = useState<boolean>(true);

  const typeOptions: SelectOption[] = typeOptionList.map((typeOption) => ({
    value: typeOption.value,
    label: typeOption.label,
  }));

  const selectedType = typeOptionList.find((typeOption) => typeOption.value === type);
  const modelOptions: SelectOption[] = selectedType
    ? selectedType.models.map((modelOption) => ({
        value: modelOption.value,
        label: modelOption.label,
      }))
    : [];

  const onTypeChange = (value: string): void => {
    setType(value);
    setModel('');
  };

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        setLoading(true);

        const options = await getAiKeyOptions();
        if (options && Array.isArray(options.types)) {
          setTypeOptionList(options.types);
        }

        if (!isNew && aiKeyId) {
          const data = await getAiKeyById(aiKeyId);
          if (data) {
            setType(data.type || '');
            setModel(data.model || '');
            setEndpoint(data.endpoint || '');
            setDeployment(data.deployment || '');
            setApiVersion(data.api_version || '');
            setIsActive(data.is_active ?? true);
            setAiKey(data.ai_key || '');
          } else {
            navigate('/404');
          }
        }
      } catch (error) {
        console.error('Error fetching AI key data:', error);
        showAlert('fail', 'Failed to load AI key data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [aiKeyId, isNew, navigate]);

  const onCancel = (): void => {
    navigate(`/organizations/${organizationId}/ai-keys`);
  };

  const onSave = async (): Promise<void> => {
    try {
      setLoading(true);

      if (isNew) {
        await createAiKey({
          type,
          model,
          ai_key: aiKey,
          endpoint: endpoint || null,
          deployment: deployment || null,
          api_version: apiVersion || null,
          is_active: isActive,
        });
      } else if (aiKeyId) {
        await updateAiKey(aiKeyId, {
          type,
          model,
          ai_key: aiKey || null,
          endpoint: endpoint || null,
          deployment: deployment || null,
          api_version: apiVersion || null,
          is_active: isActive,
        });
      }

      navigate(`/organizations/${organizationId}/ai-keys`, {
        state: {
          successMessage: isNew ? 'AI key created successfully' : 'AI key updated successfully',
        },
      });
    } catch (error) {
      console.error('Error saving AI key:', error);
      showAlert('fail', 'Failed to save AI key');
      setLoading(false);
    }
  };

  if (loading) return <PageLoader />;

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row">
        <div className="col-6 radius-yes p-2 bg-yes">
          <h2 className="h2-title">{isNew ? 'Add AI Key' : 'Edit AI Key'}</h2>

          <div className="input-block d-flex flex-row justify-between">
            <div className="input-description">
              <div className="input-title">Type</div>
              <div className="small-font">Select AI key type</div>
            </div>
            <div className="align-wrapper d-flex align-center">
              <CustomSelect
                id="ai-key-type"
                label="Type"
                options={typeOptions}
                value={type}
                multiSelect={false}
                onChange={(val: string | string[]) => onTypeChange(val as string)}
                disabled={isDisabled}
              />
            </div>
          </div>

          <div className="input-block d-flex flex-row justify-between">
            <div className="input-description">
              <div className="input-title">Model</div>
              <div className="small-font">Select AI model</div>
            </div>
            <div className="align-wrapper d-flex align-center">
              <CustomSelect
                id="ai-key-model"
                label="Model"
                options={modelOptions}
                value={model}
                multiSelect={false}
                onChange={(val: string | string[]) => setModel(val as string)}
                disabled={isDisabled || !type}
              />
            </div>
          </div>

          <div className="input-block d-flex flex-row justify-between">
            <div className="input-description">
              <div className="input-title">AI Key</div>
              <div className="small-font">
                {isNew ? 'Enter the AI key' : 'Leave empty to keep the current key'}
              </div>
            </div>
            <div className="align-wrapper d-flex align-center">
              <CustomInput
                id="ai-key-secret"
                label="AI Key"
                type="password"
                toggleVisibility={true}
                placeholder={'Enter the AI key'}
                value={aiKey}
                onChange={(_: string, value: string) => setAiKey(value)}
                disabled={isDisabled}
              />
            </div>
          </div>

          <div className="input-block d-flex flex-row justify-between">
            <div className="input-description">
              <div className="input-title">Endpoint</div>
              <div className="small-font">Enter the endpoint (optional)</div>
            </div>
            <div className="align-wrapper d-flex align-center">
              <CustomInput
                id="ai-key-endpoint"
                label="Endpoint"
                type="text"
                placeholder="Enter the endpoint"
                value={endpoint}
                onChange={(_: string, value: string) => setEndpoint(value)}
                disabled={isDisabled}
              />
            </div>
          </div>

          <div className="input-block d-flex flex-row justify-between">
            <div className="input-description">
              <div className="input-title">Deployment</div>
              <div className="small-font">Enter the deployment (optional)</div>
            </div>
            <div className="align-wrapper d-flex align-center">
              <CustomInput
                id="ai-key-deployment"
                label="Deployment"
                type="text"
                placeholder="Enter the deployment"
                value={deployment}
                onChange={(_: string, value: string) => setDeployment(value)}
                disabled={isDisabled}
              />
            </div>
          </div>

          <div className="input-block d-flex flex-row justify-between">
            <div className="input-description">
              <div className="input-title">API Version</div>
              <div className="small-font">Enter the API version (optional)</div>
            </div>
            <div className="align-wrapper d-flex align-center">
              <CustomInput
                id="ai-key-api-version"
                label="API Version"
                type="text"
                placeholder="Enter the API version"
                value={apiVersion}
                onChange={(_: string, value: string) => setApiVersion(value)}
                disabled={isDisabled}
              />
            </div>
          </div>

          <div className="input-row input-block d-flex flex-row justify-between">
            <div className="input-description">
              <div className="input-title">Active</div>
              <div className="small-font">Enable or disable this AI key</div>
            </div>
            <div className="align-wrapper d-flex align-center">
              <CustomToggle
                id="ai-key-active"
                label={isActive ? 'Active' : 'Inactive'}
                checked={isActive}
                onChange={(_id: string, value: boolean) => setIsActive(value)}
                disabled={isDisabled}
              />
            </div>
          </div>

          <div className="buttons-container justify-end gap-12">
            <Button background="color2" size="medium" disabled={isDisabled} onClick={onSave}>
              {isNew ? 'Create' : 'Save'}
            </Button>
            <Button variant="danger" size="medium" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AiKeyDetail() {
  return (
    <ConfirmationProvider>
      <AiKeyApiProvider>
        <AiKeyDetailContent />
      </AiKeyApiProvider>
    </ConfirmationProvider>
  );
}

export default AiKeyDetail;
